import { createContext, useCallback, useEffect, useRef, useState, startTransition } from "react";
import { useDataProvider, useNotify, useRefresh, useUpdate } from "react-admin";
import { handleHttpError } from "shared/src/utils/errorHandler";

export const FileManagerContext = createContext();
export const FileManagerContextProvider = ({
    onClose,
    arrival,
    media,
    isUploading = true,
    children,
}) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [update] = useUpdate();
    const dataProvider = useDataProvider();
    const [data, setData] = useState();
    const [page, setPage] = useState(1);
    const frameRef = useRef();
    const [isCheckingArrivalForm, setIsCheckingArrivalForm] = useState(false);
    const [isCheckingProductForms, setIsCheckingProductForms] = useState(false);

    useEffect(() => {
        return () => {
            if (!frameRef.current) return;
            if (typeof cancelAnimationFrame === "function") {
                cancelAnimationFrame(frameRef.current);
            } else {
                clearTimeout(frameRef.current);
            }
        };
    }, []);

    const scheduleUpdate = useCallback(
        (updater) => {
            if (frameRef.current) {
                if (typeof cancelAnimationFrame === "function") {
                    cancelAnimationFrame(frameRef.current);
                } else {
                    clearTimeout(frameRef.current);
                }
            }

            const runUpdate = () => {
                frameRef.current = undefined;
                if (typeof startTransition === "function") {
                    startTransition(() => setData(updater));
                } else {
                    setData(updater);
                }
            };

            if (typeof requestAnimationFrame === "function") {
                frameRef.current = requestAnimationFrame(runUpdate);
            } else {
                frameRef.current = setTimeout(runUpdate, 16);
            }
        },
        [setData]
    );

    const matchData = (data, arrival) => {
        return {
            arrivalData: {
                supplier:
                    arrival?.supplier?.id ||
                    data?.mapped_supplier_id ||
                    data?.document_issuer?.name ||
                    "",
                orderDate: data.invoice_date || arrival.orderDate || null,
                deliveryDate: data.due_date || arrival.deliveryDate || null,
                purchaseOrderNumber: arrival.purchaseOrderNumber || data.invoice_number || "",
                purchasePrice: arrival.purchasePrice || data.total_amount || 0,
                defectQuantity: arrival.defectQuantity || 0, // No direct mapping
                comment: arrival.comment || data.comments || "",
                sourceSupplierName: data?.document_issuer?.name || "",
            },
            productList: (data?.line_items ?? []).map((item) => ({
                isMassProduct: (item?.serial_numbers?.length ?? 0) > 0 ? "0" : "1",
                manufacturer: item?.mapped_manufacturer_id
                    ? { id: item.mapped_manufacturer_id }
                    : (item?.manufacturer ?? ""),
                storageLocation: item?.storage_location ?? "",
                weight: item?.weight ?? 0,
                product: item?.mapped_product_id ? { id: item.mapped_product_id } : (item?.description ?? ""),
                purchasePrice: item?.unit_price ?? 0,
                quantity: item?.quantity ?? 0,
                serialNumbers: item?.serial_numbers ?? [],
                sourceDescription: item?.description ?? "",
                sourceArticleNumber: item?.article_number ?? "",
            })),
        };
    };

    const validateArrivalData = (values) => {
        let isValid = true;
        const validSupplier = !isNaN(values.supplier) || values.supplier?.id;
        if (
            !validSupplier ||
            !values.deliveryDate ||
            !values.orderDate ||
            !values.purchaseOrderNumber ||
            !values.purchasePrice
        ) {
            isValid = false;
        }
        if (!isValid) {
            setPage(1);
            setIsCheckingArrivalForm(true);
        }
        return isValid;
    };

    const previousPage = () => {
        setPage(page - 1);
    };

    const nextPage = () => {
        if (page === 1) {
            const isValid = validateArrivalData(data.arrivalData);
            if (isValid) {
                setPage(page + 1);
                updateArrival();
            }
        } else {
            setPage(page + 1);
        }
    };

    const handleClose = () => {
        onClose();
        setPage(1);
    };

    const validateProductData = (productsArr) => {
        let isValid = true;
        productsArr.forEach((values, index) => {
            if (!isValid) return;
            if (!values.product || (!values.product.id && !values.productString)) {
                isValid = false;
            }
            if (!values.manufacturer || !values.manufacturer.id) {
                isValid = false;
            }
            if (!values.purchasePrice && values.purchasePrice !== 0) {
                isValid = false;
            }
            if (
                !values.isMassProduct &&
                (!values.serialNumbers || values.serialNumbers.length === 0)
            ) {
                isValid = false;
            }
            if (!isValid) {
                setPage(2);
                setIsCheckingProductForms(true);
            }
        });
        return isValid;
    };

    const updateArrival = (callback = () => {}) => {
        update(
            "arrivals",
            {
                id: arrival.id,
                data: {
                    supplier: data.arrivalData.supplier,
                    purchaseOrderNumber: data.arrivalData?.purchaseOrderNumber || "",
                    purchasePrice: data.arrivalData.purchasePrice,
                    orderDate: data.arrivalData.orderDate,
                    deliveryDate: data.arrivalData.deliveryDate,
                    defectQuantity: data.arrivalData.defectQuantity,
                    comment: data.arrivalData.comment,
                },
            },
            {
                onSuccess: () => {
                    callback();
                },
                onError: (e) => {
                    handleHttpError(e, notify);
                },
            }
        );
    };

    const createProducts = () => {
        const supplierId = data.arrivalData?.supplier?.id ?? data.arrivalData?.supplier;
        const payload = {
            mappingSupplierName: data.arrivalData?.sourceSupplierName || null,
            productData: data.productList.flatMap((product) => {
                const hasSerials =
                    Array.isArray(product.serialNumbers) && product.serialNumbers.length > 0;
                if (hasSerials) {
                    return product.serialNumbers.map((serialNumber) => ({
                        serialNumber,
                        supplier: supplierId,
                        manufacturer: product.manufacturer.id,
                        weight: product.weight,
                        purchasePrice: product.purchasePrice,
                        product: product.product.id,
                        productString: product.productString ?? null,
                        quantity: 1,
                        sourceDescription: product.sourceDescription ?? null,
                        sourceArticleNumber: product.sourceArticleNumber ?? null,
                    }));
                }
                return [
                    {
                        serialNumber: product.serialNumber ?? null,
                        supplier: supplierId,
                        manufacturer: product.manufacturer.id,
                        weight: product.weight,
                        purchasePrice: product.purchasePrice,
                        product: product.product.id,
                        productString: product.productString ?? null,
                        quantity: product.quantity ?? 0,
                        sourceDescription: product.sourceDescription ?? null,
                        sourceArticleNumber: product.sourceArticleNumber ?? null,
                    },
                ];
            }),
        };
        dataProvider
            .performAction("arrivals", {
                id: arrival.id,
                actionName: "createProductArrivals",
                data: payload,
            })
            .then(() => {
                refresh();
                setPage(1);
                onClose();
            })
            .catch((e) => {
                handleHttpError(e, notify);
            });
    };

    const submit = () => {
        const isArrivalDataValid = validateArrivalData(data.arrivalData);
        if (!isArrivalDataValid) return;

        const isProductDataValid = validateProductData(data.productList);
        if (!isProductDataValid) return;

        updateArrival(createProducts);
    };

    return (
        <FileManagerContext.Provider
            value={{
                data,
                setData,
                scheduleUpdate,
                page,
                setPage,
                nextPage,
                previousPage,
                submit,
                media,
                isUploading,
                onClose,
                updateArrival,
                handleClose,
                matchData: useCallback(
                    (extractedData) => matchData(extractedData, arrival),
                    [arrival]
                ),
                isCheckingArrivalForm,
                setIsCheckingArrivalForm,
                isCheckingProductForms,
                setIsCheckingProductForms,
            }}
        >
            {children}
        </FileManagerContext.Provider>
    );
};
